/**
 * bQuest Preferred Providers panel for professional surfaces (Feature 096).
 *
 * Two variants per the design handoff:
 *  - `grid`  — My Network page: card grid with Message + View Profile.
 *  - `row`   — FP dashboard Network tab: compact rows with Message + Refer.
 *
 * Self-contained: fetches the global curated list, shows a skeleton while
 * loading, and renders nothing when the list is empty or fails to load
 * (curated content must never break a host surface).
 */

import { Mail, Share2 } from 'lucide-react';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { getFileUrl } from '@/shared/hooks/useFileUpload';
import { CuratedSection } from './CuratedSection';
import { PreferredBadge } from './PreferredBadge';
import { useCuratedContent } from './useCuratedContent';
import type { CuratedProvider } from './types';

const SUBTITLE_GRID =
  'Vetted professionals our team recommends across every category. Not filtered by your search above.';
const SUBTITLE_ROW = 'Vetted professionals you can message or refer a client to directly.';

type CuratedProvidersPanelProps = {
  variant: 'grid' | 'row';
  /** Render the "Not part of your connections" divider above the panel. */
  divider?: boolean;
  onMessage: (userId: string) => void;
  onViewProfile?: (serviceProviderId: string) => void;
  onRefer?: (serviceProviderId: string, providerName: string) => void;
};

export function CuratedProvidersPanel({
  variant,
  divider = false,
  onMessage,
  onViewProfile,
  onRefer,
}: CuratedProvidersPanelProps) {
  const { data, isLoading, isError } = useCuratedContent();

  if (isLoading) {
    return (
      <div className="mt-8 space-y-3" data-testid="curated-providers-panel-skeleton">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  const providers = data?.preferredProviders ?? [];
  if (isError || providers.length === 0) {
    return null;
  }

  return (
    <div data-testid="curated-providers-panel">
      {divider && (
        <div className="mt-12 mb-2 flex items-center gap-4" aria-hidden="true">
          <div className="h-px flex-1 bg-gray-200" />
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
            Not part of your connections
          </span>
          <div className="h-px flex-1 bg-gray-200" />
        </div>
      )}
      <CuratedSection
        title="bQuest Preferred Providers"
        subtitle={variant === 'grid' ? SUBTITLE_GRID : SUBTITLE_ROW}
      >
        {variant === 'grid' ? (
          <div className="grid md:grid-cols-2 gap-6">
            {providers.map((provider) => (
              <GridCard
                key={provider.id}
                provider={provider}
                onMessage={onMessage}
                onViewProfile={onViewProfile}
              />
            ))}
          </div>
        ) : (
          <div className="rounded-lg bg-white border divide-y">
            {providers.map((provider) => (
              <ProviderRow
                key={provider.id}
                provider={provider}
                onMessage={onMessage}
                onRefer={onRefer}
              />
            ))}
          </div>
        )}
      </CuratedSection>
    </div>
  );
}

const initialsOf = (name: string): string =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

const photoUrlOf = (provider: CuratedProvider): string | null =>
  provider.profilePhoto
    ? provider.profilePhoto.startsWith('http')
      ? provider.profilePhoto
      : getFileUrl(provider.profilePhoto)
    : null;

const displayNameOf = (provider: CuratedProvider): string =>
  provider.serviceProvider?.companyName || provider.name || 'Unknown Professional';

type CardProps = {
  provider: CuratedProvider;
  onMessage: (userId: string) => void;
  onViewProfile?: (serviceProviderId: string) => void;
  onRefer?: (serviceProviderId: string, providerName: string) => void;
};

function GridCard({ provider, onMessage, onViewProfile }: CardProps) {
  const name = displayNameOf(provider);
  const photoUrl = photoUrlOf(provider);
  const categories = provider.serviceProvider?.categories ?? [];

  return (
    <Card className="p-6 bg-white">
      <div className="flex items-start gap-4 mb-4">
        <Avatar className="w-14 h-14 flex-shrink-0">
          {photoUrl ? (
            <img src={photoUrl} alt={name} className="w-full h-full object-cover" />
          ) : (
            <AvatarFallback className="bg-[#026857] text-white">{initialsOf(name)}</AvatarFallback>
          )}
        </Avatar>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">{name}</h3>
          <div className="mb-2">
            <PreferredBadge />
          </div>
          {provider.name && provider.name !== name && (
            <p className="text-sm text-gray-600 truncate">{provider.name}</p>
          )}
          {categories.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {categories.map((c) => (
                <span
                  key={c.category.id}
                  className="rounded bg-[#026857]/10 px-2 py-0.5 text-xs font-medium text-[#026857]"
                >
                  {c.category.name}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-2">
        <Button
          className="flex-1 bg-[#026857] hover:bg-[#025247]"
          onClick={() => onMessage(provider.id)}
        >
          <Mail className="w-4 h-4 mr-2" />
          Message
        </Button>
        {onViewProfile && (
          <Button
            variant="outline"
            className="flex-1"
            disabled={!provider.serviceProvider?.id}
            onClick={() =>
              provider.serviceProvider?.id && onViewProfile(provider.serviceProvider.id)
            }
          >
            View Profile
          </Button>
        )}
      </div>
    </Card>
  );
}

function ProviderRow({ provider, onMessage, onRefer }: CardProps) {
  const name = displayNameOf(provider);
  const photoUrl = photoUrlOf(provider);

  return (
    <div className="flex items-center gap-3 p-3">
      <Avatar className="w-10 h-10 flex-shrink-0">
        {photoUrl ? (
          <img src={photoUrl} alt={name} className="w-full h-full object-cover" />
        ) : (
          <AvatarFallback className="bg-[#fbecca] text-[#8a6a16] text-xs">
            {initialsOf(name)}
          </AvatarFallback>
        )}
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <h4 className="text-sm font-medium text-gray-900 truncate">{name}</h4>
          <PreferredBadge />
        </div>
        {provider.name && provider.name !== name && (
          <p className="text-xs text-gray-600 truncate">{provider.name}</p>
        )}
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 text-gray-600 hover:text-[#026857] hover:bg-[#026857]/10"
          title="Message professional"
          onClick={() => onMessage(provider.id)}
        >
          <Mail className="w-4 h-4" />
        </Button>
        {onRefer && provider.serviceProvider?.id && (
          <Button
            variant="ghost"
            size="sm"
            className="gap-2 text-gray-600 hover:text-[#026857] hover:bg-[#026857]/10"
            title="Refer provider to clients"
            onClick={() => onRefer(provider.serviceProvider!.id, name)}
          >
            <Share2 className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}
